import { Button, Typography } from '@breetometer/components';
import { ArrowIcon } from 'BreetAssets';
import { PAGINATION_DOTS } from 'BreetHelpers';
import { usePagination } from 'BreetHooks';
import { useCallback } from 'react';

import { PageLimitList } from './PageLimitList';
import { getDocItemsIdentifier, type PaginationProps } from './Pagination.helpers';

const Pagination = ({
	onPageChange,
	isLoading,
	totalDocItems,
	siblingCount = 1,
	currentPage,
	pageLimit = 50,
	className,
	docItemsIdentifier,
}: PaginationProps) => {
	const paginationRange = usePagination({
		currentPage,
		totalDocItems,
		siblingCount,
		pageLimit,
	});

	const canDataBePaginated = !isLoading && !!currentPage && paginationRange.length >= 2;
	const lastPage = paginationRange[paginationRange.length - 1];
	const leftDisableCheck = currentPage === 1 || !canDataBePaginated;
	const rightDisableCheck = currentPage === lastPage || !canDataBePaginated;

	const handleNextClick = useCallback(() => {
		if (isLoading) return;
		onPageChange?.({
			currentPage: currentPage + 1,
			pageLimit,
			totalDocItems,
		});
	}, [currentPage, isLoading, onPageChange, pageLimit, totalDocItems]);

	const handlePreviousClick = useCallback(() => {
		if (isLoading) return;
		onPageChange?.({ currentPage: currentPage - 1, pageLimit, totalDocItems });
	}, [currentPage, isLoading, onPageChange, pageLimit, totalDocItems]);

	const handleFirstClick = useCallback(() => {
		if (isLoading) return;
		onPageChange?.({ currentPage: 1, pageLimit, totalDocItems });
	}, [isLoading, onPageChange, pageLimit, totalDocItems]);

	const handleLastClick = useCallback(() => {
		if (isLoading) return;
		onPageChange?.({ currentPage: lastPage, pageLimit, totalDocItems });
	}, [isLoading, lastPage, onPageChange, pageLimit, totalDocItems]);

	const handlePageNumberClick = useCallback(
		(pageNumber: number) => () => {
			if (isLoading) return;
			onPageChange?.({ currentPage: pageNumber, pageLimit, totalDocItems });
		},
		[isLoading, onPageChange, pageLimit, totalDocItems]
	);

	const handlePagelimitClick = useCallback(
		(limit: number) => {
			onPageChange?.({ currentPage: 1, pageLimit: limit, totalDocItems });
		},
		[onPageChange, totalDocItems]
	);
	const paginationListClass = className ? `bt_pagination_list ${className}` : 'bt_pagination_list';

	return (
		<div className='bt_pagination'>
			{!!totalDocItems && (
				<Typography
					as='span'
					variant='small_header'
					className='bt_pagination_totalDocItems bg-primary-600 color-primary-50'
					title='Total number of items rendered in the table'
				>
					{totalDocItems}{' '}
					{docItemsIdentifier ? (
						<span className='identifier color-primary-50'>{getDocItemsIdentifier(totalDocItems, docItemsIdentifier)}</span>
					) : null}
				</Typography>
			)}

			<ul className={paginationListClass}>
				<Button
					leadingIcon={<ArrowIcon variant='leftDoubleCaret' />}
					id='bt_pagination_first'
					label=''
					variant='outline'
					disabled={leftDisableCheck}
					onClick={handleFirstClick}
				/>
				<Button
					leadingIcon={<ArrowIcon variant='leftCaret' />}
					id='bt_pagination_prev'
					label=''
					variant='outline'
					disabled={leftDisableCheck}
					onClick={handlePreviousClick}
				/>
				{paginationRange.map((pageNumber, index) => {
					if (pageNumber === PAGINATION_DOTS) {
						return (
							<li
								className='paginationItem_dots'
								key={pageNumber + index}
							>
								&#8230;
							</li>
						);
					}

					return (
						<div
							key={pageNumber}
							className={currentPage === pageNumber ? 'btn-selected' : 'btn-not-selected'}
						>
							<Button
								label={String(pageNumber)}
								onClick={handlePageNumberClick(pageNumber)}
								disabled={!canDataBePaginated}
								variant='outline'
								className={currentPage === pageNumber ? 'bg-tertiary-300 color-neutral-50' : ''}
							/>
						</div>
					);
				})}

				<Button
					leadingIcon={<ArrowIcon variant='rightCaret' />}
					label=''
					variant='outline'
					disabled={rightDisableCheck}
					onClick={handleNextClick}
				/>
				<Button
					leadingIcon={<ArrowIcon variant='rightDoubleCaret' />}
					id='bt_pagination_last'
					label=''
					variant='outline'
					disabled={rightDisableCheck}
					onClick={handleLastClick}
				/>
			</ul>

			<div className='bt_pagination_pagelimit'>
				<PageLimitList
					pageLimit={pageLimit}
					onLimitChange={handlePagelimitClick}
					isLoading={isLoading}
				/>
			</div>
		</div>
	);
};

export default Pagination;
